import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild, ElementRef } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { NgbCalendar, NgbDate, NgbDatepicker, NgbDatepickerModule, NgbDropdownModule, NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { PerfectScrollbarService } from 'src/app/_services/perfect-scrollbar.service';
import { environment } from '../../../environments/environment';
import { ChevronDownIconComponent } from '../../shared/components/icons';
import { PopupManager, PopupType } from '../popup-manager';
import { ApiControllerService } from '../api-controller-service';
import { AlertService } from 'src/app/_services/alert.service';
import { MerchantIdDialogComponent } from './merchant-id-dialog/merchant-id-dialog.component';
import { ExportService } from 'src/app/_services/export.service';
import { HttpErrorResponse } from '@angular/common/http';
import { parseHttpErrorMessage } from 'src/app/_helpers/http-error.utils';
import { EnvironmentOpt } from 'src/app/shared/constants/app.constants';
import { AuthService } from 'src/app/_services/auth.service';
const { defaultPageSizeArr, defaultPageSize } = environment;

interface User {
    id: string;
    fullName: string;
    emailId: string;
    status: Status;
    securityType: string;
    roleName: Role;
    lastLogin: string;
    merchantId?: string;
    merchantName?: string;
}

export enum FilterField {
    NAME = 'Name',
    EMAIL = 'Email',
    ROLE = 'Role',
    STATUS = 'Status',
    MERCHANT_ID = 'Merchant Id',
    MERCHANT_NAME = 'Merchant Name'
}

enum FilterType {
    DATE_RANGE = 'date-range',
    DROPDOWN = 'dropdown',
    TEXT_INPUT = 'text-input'
}

class DropdownItem {
    label: string;
    value: any;
    visible: boolean = true;
    selected: boolean = false;
    children?: DropdownItem[];
    expanded: boolean = false;

    constructor(label: string, value: any, children?: DropdownItem[]) {
        this.label = label;
        this.value = value;
        this.children = children;
    }

    hasChildren(): boolean {
        return this.children !== undefined && this.children.length > 0;
    }

    getAllDescendants(): DropdownItem[] {
        if (!this.hasChildren()) {
            return [];
        }

        let descendants: DropdownItem[] = [];
        for (const child of this.children!) {
            descendants.push(child, ...child.getAllDescendants());
        }
        return descendants;
    }

    getAllLeafNodes(): DropdownItem[] {
        if (!this.hasChildren()) {
            return [this];
        }

        let leafNodes: DropdownItem[] = [];
        for (const child of this.children!) {
            leafNodes.push(...child.getAllLeafNodes());
        }
        return leafNodes;
    }
}

export enum Status {
    APPROVE_PENDING = 'APPROVE_PENDING',
    ACTIVE = 'ACTIVE',
    REJECTED = 'REJECTED'
}

enum Role {
    PAY10_ADMIN = 'Developer Pay10 Admin',
    DEVELOPER_ADMIN = 'Developer Admin',
    DEVELOPER = 'Developer'
}

@Component({
    selector: 'app-user-management',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        AgGridAngular,
        NgbPaginationModule,
        NgbDropdownModule,
        NgbDatepickerModule,
        ChevronDownIconComponent,
        MerchantIdDialogComponent
    ],
    templateUrl: './user-management.component.html',
    styleUrls: ['./user-management.component.css']
})
export class UserManagementComponent implements OnInit {
    constructor(
        private readonly apiControllerService: ApiControllerService,
        private readonly calendar: NgbCalendar,
        private readonly cdr: ChangeDetectorRef,
        private readonly alertService: AlertService,
        private readonly perfectScrollbarService: PerfectScrollbarService,
        private readonly authService: AuthService,
    ) { }

    role: string = '';

    ngOnInit() {
        this.role = this.authService.getRole();
        this.perfectScrollbarService.initializeScrollableList();
        this.columnDefs = this.columnDefs.map((c, i) => ({
            ...c,
            colId: (c as any).colId || c.headerName || `col_${i}`
        }));
        // Show merchant columns only for PAY10_ADMIN
        if (this.role === Role.PAY10_ADMIN) {
            this.columnDefs = this.columnDefs.map(col => {
                if (col.field === 'merchantId' || col.field === 'merchantName') {
                    return { ...col, hide: false };
                }
                return col;
            });
        }
    }

    ngAfterViewInit() {
        this.initializeDualCalendars();
    }

    readonly FilterType = FilterType;
    private readonly popupManager: PopupManager = new PopupManager();
    
    // Merchant ID Dialog state
    isMerchantIdDialogVisible = false;
    pendingApprovalUserId: string | null = null;

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        const target = event.target as Element;
        const currentPopup = this.popupManager.getCurrent();

        // Close action menu if clicking outside
        if (!target.closest('.actions-dropdown')) {
            this.openedActionRowId = null;
        }

        switch (currentPopup) {
            case PopupType.CUSTOMIZE: {
                const customizeDropdown = target.closest('.customize-dropdown');
                if (!customizeDropdown) {
                    this.popupManager.closeAll();
                }
                break;
            }
            case PopupType.DATE_PICKER: {
                const datePickerArea = target.closest('.date-range-picker');
                if (!datePickerArea) {
                    this.popupManager.closeAll();
                }
                break;
            }
            default:
                break;
        }
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscapeKey(event: KeyboardEvent) {
        this.popupManager.closeAll();
    }

    noRowsTemplate = `
    <div class="no-results-overlay">
      <img src="assets/img/start.svg" alt="No results" class="no-results-icon" />
      <div class="no-results-title">No Results Found</div>
      <div class="no-results-description">There are no users that match your search.</div>
    </div>
  `;

    private gridApi!: GridApi<User>;
    onGridReady(params: GridReadyEvent<User>) {
        this.gridApi = params.api;
        this.fetchUsers();

        setTimeout(() => {
            this.perfectScrollbarService.initializeAgGrid();
        }, 100);
    }

    openedActionRowId: string | null = null;
    actionMenuPosition = { top: 0, left: 0 };

    toggleActionMenu(rowId: string, event: Event) {
        event.stopPropagation();

        if (this.openedActionRowId === rowId) {
            this.openedActionRowId = null;
        } else {
            this.openedActionRowId = rowId;

            // Calculate position for dropdown menu
            const button = (event.target as HTMLElement).closest('.actions-trigger');
            if (button) {
                const rect = button.getBoundingClientRect();
                this.actionMenuPosition = {
                    top: rect.bottom + 4,
                    left: rect.right - 160 // 160px is the min-width of the menu
                };
            }
        }
    }

    isActionMenuOpen(rowId: string): boolean {
        return this.openedActionRowId === rowId;
    }

    get selectedRow(): User | undefined {
        return this.userData.find(row => row.id === this.openedActionRowId);
    }

    onApproveAction() {
        const row = this.selectedRow;
        console.debug("onApproveAction:", row?.id);
        if (!row) return;
        this.openedActionRowId = null;
        console.log('Approve User:', row);
        
        // Open merchant ID dialog
        if (row.roleName === Role.DEVELOPER_ADMIN) {
            this.pendingApprovalUserId = row.id;
            this.isMerchantIdDialogVisible = true;
        } else {
            this.pendingApprovalUserId = row.id;
            this.updateUserStatus(Status.ACTIVE, row.fullName);
        }
    }
    
    handleMerchantIdSubmit(merchantId: string) {
        const fullName = this.userData.find(row => row.id === this.pendingApprovalUserId)?.fullName ?? '';
        this.updateUserStatus(Status.ACTIVE, fullName, merchantId);
    }

    private updateUserStatus(status: Status, requsetedName: string, merchantId?: string) {
        if (!this.pendingApprovalUserId) return;
        const currentEnv = localStorage.getItem('environment')?.toUpperCase();
        this.apiControllerService.updateSignupRequestResult(
            this.pendingApprovalUserId, 
            status, 
            currentEnv ?? EnvironmentOpt.Sandbox.toUpperCase(), 
            merchantId
        ).subscribe({
            next: (resp: any) => {
                console.log('updateUserStatus: ', resp);
                if(resp != null && resp.response?.status !== 200) {
                    // failed
                    this.alertService.customInfoPopup({
                        icon: 'assets/img/failure.svg',
                        title: `Failed to ${status === Status.ACTIVE ? 'Approve' : 'Reject'} User`,
                        description: `Failed to ${status === Status.ACTIVE ? 'approve' : 'reject'} signup request. Reason: ${resp?.message || 'Unknown error'}. Please try again later.`,
                        primaryButton: {
                            text: 'OK',
                            action: () => {
                                this.fetchUsers();
                            }
                        }
                    });
                    this.isMerchantIdDialogVisible = false;
                } else {
                    this.alertService.customInfoPopup({
                        icon: `${status === Status.ACTIVE ? 'assets/img/Tick.svg' : 'assets/img/failure.svg'}`,
                        title: `${requsetedName} Has Been ${status === Status.ACTIVE ? 'Approved' : 'Rejected'}`,
                        primaryButton: {
                            text: 'Done',
                            action: () => {
                                this.fetchUsers();
                            }
                        }
                    });
                }
                this.pendingApprovalUserId = null;
            },
            error: (error: HttpErrorResponse) => {
                this.alertService.customInfoPopup({
                    icon: 'assets/img/failure.svg',
                    title: `Failed to ${status === Status.ACTIVE ? 'Approve' : 'Reject'} User`,
                    description: `Failed to ${status === Status.ACTIVE ? 'approve' : 'reject'} signup request. Reason: ${parseHttpErrorMessage(error)}.${error.status ===403 ? '' : ' Please try again later.'}`,
                    primaryButton: {
                        text: 'OK'
                    }
                });
                this.isMerchantIdDialogVisible = false;
                this.pendingApprovalUserId = null;
            }
        });
    }
    
    handleMerchantIdDialogClose() {
        this.isMerchantIdDialogVisible = false;
        this.pendingApprovalUserId = null;
    }

    onRejectAction() {
        const row = this.selectedRow;
        console.debug("onRejectAction:", row?.id);
        if (!row) return;
        this.openedActionRowId = null;
        console.log('Reject User:', row);
        this.pendingApprovalUserId = row.id;
        this.updateUserStatus(Status.REJECTED, row.fullName);
    }

    // Methods to determine which actions should be visible based on user data
    shouldShowApproveAction(user: User | undefined): boolean {
        if (!user) return false;
        // Approve is only available for APPROVE_PENDING users
        return user.status === Status.APPROVE_PENDING;
    }

    shouldShowRejectAction(user: User | undefined): boolean {
        if (!user) return false;
        // Reject is only available for APPROVE_PENDING users
        return user.status === Status.APPROVE_PENDING;
    }

    getRowId = (params: { data: User }) => String(params.data?.id);

    defaultColDef: ColDef = {
        filter: false,
        sortable: false,
        resizable: true,
        autoHeight: true,
        suppressHeaderMenuButton: true,
        flex: 1
    };

    columnDefs: ColDef[] = [
        { field: 'fullName', sortable: false, lockVisible: true, headerName: 'Name', resizable: true },
        { field: 'emailId', sortable: false, lockVisible: true, headerName: 'Email', resizable: true },
        { field: 'merchantId', sortable: false, lockVisible: true, headerName: 'Merchant Id', resizable: true, hide: true },
        { field: 'merchantName', sortable: false, lockVisible: true, headerName: 'Merchant Name', resizable: true, hide: true },
        { 
            field: 'roleName', 
            sortable: false, 
            lockVisible: true, 
            headerName: 'Role', 
            resizable: true,
            valueFormatter: (params: any) => {
                const role = params.value;
                if (role === Role.PAY10_ADMIN) return 'Super Admin';
                if (role === Role.DEVELOPER_ADMIN) return 'Admin';
                return role;
            }
        },
        { 
            field: 'status', 
            sortable: false, 
            lockVisible: true, 
            headerName: 'Status', 
            resizable: true,
            valueFormatter: (params: any) => {
                const status : Status = params.value || '';
                if(Object.values(Status).includes(status)) {
                    // Replace underscores with spaces and capitalize first letter, rest lowercase
                    return status
                    .replace(/_/g, ' ')
                    .toLowerCase()
                    .replace(/^\w/, (c: string) => c.toUpperCase());
                } else {
                    return 'Initiated';
                }
            }
        },
        { field: 'lastLogin', sortable: false, lockVisible: true, headerName: 'Last Login Date & Time', resizable: true },
        {
            field: 'securityType', 
            sortable: false, 
            lockVisible: true, 
            headerName: '2FA', 
            resizable: true,  
            valueFormatter: (params: any) => {
                    return params.value ?? 'Disabled';
                }

        },
        {
            headerName: 'Actions',
            colId: 'actions',
            width: 90,
            lockVisible: true,
            pinned: 'right',
            sortable: false,
            resizable: false,
            cellRenderer: (params: any) => {
                if (!this.shouldShowApproveAction(params.data) && !this.shouldShowRejectAction(params.data)) {
                    return '';
                }
                const container = document.createElement('div');
                container.className = 'actions-cell';
                container.innerHTML = `
          <div class="actions-dropdown" data-row-id="${params.data.id}">
            <button class="actions-trigger" type="button">
              <span class="dots"></span>
            </button>
          </div>
        `;

                const button = container.querySelector('.actions-trigger');
                button?.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.toggleActionMenu(params.data.id, e);
                });

                return container;
            }
        }
    ]

    userData: User[] = [];
    totalItems: number = 0;
    totalItemsWithoutFilters: number = 0;
    isViewOnlyAccess: boolean = false; // Set to true to show access notice

    // --- History Panel State ---
    showHistoryPanel: boolean = false;
    historyItems: any[] = [];
    historyPageNumber: number = 0; // zero-based to match backend
    historyPageSize: number = 20;
    historyTotalItems: number = 0;
    historyLoading: boolean = false;
    historyHasMore: boolean = true;
    @ViewChild('historyScrollContainer') historyScrollContainer!: ElementRef<HTMLDivElement>;

    fetchUsers() {
        this.apiControllerService.getUsers(this.rowsPerPage, this.currentPage - 1, this.filters).subscribe({
            next: (resp: any) => {
                if(resp.response?.status === 200) {
                    this.userData = resp?.response?.data?.content;
                    this.totalItems = resp?.response?.data?.totalElements;
                    if (this.filters.size === 0) {
                        this.totalItemsWithoutFilters = this.totalItems;
                    }
                }
            },
            error: (error: HttpErrorResponse) => {
                console.error("fetching users failed:", error);
            }
        });
    }

    selectedUser: User | null = null;

    readonly pageSizeOptions: number[] = defaultPageSizeArr;
    currentPage: number = 1;
    rowsPerPage: number = defaultPageSize;

    filters: Map<FilterField, any> = new Map();

    readonly filterFieldOptions: FilterField[] = [
        FilterField.NAME,
        FilterField.EMAIL,
        FilterField.ROLE,
        FilterField.STATUS,
        FilterField.MERCHANT_ID,
        FilterField.MERCHANT_NAME
    ];

    get visibleFilterFieldOptions(): FilterField[] {
        if (this.role === Role.PAY10_ADMIN) {
            return this.filterFieldOptions;
        }
        return this.filterFieldOptions.filter(f => f !== FilterField.MERCHANT_ID && f !== FilterField.MERCHANT_NAME);
    }

    filterField: FilterField = this.filterFieldOptions[0];

    onFilterDropdownClick(dropdown: any, event: Event) {
        this.popupManager.closeAll();
        setTimeout(() => {
            const isOpen = dropdown.isOpen();
            if (isOpen) {
                this.popupManager.toggle(PopupType.FILTER_FIELD);
            }
        }, 0);
    }

    onFilterValueDropdownOpen() {
        setTimeout(() => {
            this.perfectScrollbarService.initializeScrollableList(['.filter-value-area .recursive-menu-scrollable-list']);
        });
    }

    putFilterField(filterKey: FilterField) {
        this.filterField = filterKey;
        this.initFilterValues();
        this.customizeSearch = '';
        this.searchInDropdownMenu();
    }

    private initFilterValues() {
        this.dateFromFilter = null;
        this.dateToFilter = null;
        this.selectedDateRange = '';

        const allItems = this.getAllDropdownItems(this.currentDropdownOptions);
        allItems.forEach(option => {
            option.selected = false;
            option.visible = true;
            option.expanded = false;
        });

        this.dropdownFilterValues.clear();
        this.textInputFilter = '';
        this.showSelectedMenuItem = false;
        this.searchTextInDropdownMenu = '';
    }

    get currentFilterType(): FilterType {
        switch (this.filterField) {
            case FilterField.STATUS:
            case FilterField.ROLE:
                return FilterType.DROPDOWN;
            case FilterField.NAME:
            case FilterField.EMAIL:
            case FilterField.MERCHANT_ID:
            case FilterField.MERCHANT_NAME:
            default:
                return FilterType.TEXT_INPUT;
        }
    }

    dateFromFilter: NgbDate | null = null;
    dateToFilter: NgbDate | null = null;
    dropdownFilterValues = new Set<string>();
    textInputFilter: string = '';

    selectedDateRange: string = '';
    leftCalendarDate: NgbDate | null = null;
    rightCalendarDate: NgbDate | null = null;
    leftCalendarMinDate: NgbDate | null = null;
    minSelectableDate!: NgbDate;
    maxSelectableDate!: NgbDate;
    @ViewChild('leftDatePicker') leftDatePicker!: NgbDatepicker;
    @ViewChild('rightDatePicker') rightDatePicker!: NgbDatepicker;

    private initializeDualCalendars() {
        const today = this.calendar.getToday();
        const previousMonth = this.calendar.getPrev(today, 'm', 1);

        this.minSelectableDate = this.calendar.getPrev(today, 'y', 3);
        this.maxSelectableDate = today;

        this.leftCalendarDate = previousMonth;
        this.rightCalendarDate = today;
        this.leftCalendarMinDate = this.minSelectableDate;

        if (this.leftDatePicker && this.leftCalendarDate) {
            this.leftDatePicker.navigateTo(this.leftCalendarDate);
        }
        if (this.rightDatePicker && this.rightCalendarDate) {
            this.rightDatePicker.navigateTo(this.rightCalendarDate);
        }
        this.cdr.detectChanges();
    }

    get showDatePicker(): boolean {
        return this.popupManager.isOpen(PopupType.DATE_PICKER);
    }

    toggleDatePicker() {
        this.popupManager.toggle(PopupType.DATE_PICKER);
    }

    readonly statusOptions: DropdownItem[] = [
        new DropdownItem('Approve Pending', Status.APPROVE_PENDING),
        new DropdownItem('Active', Status.ACTIVE),
        new DropdownItem('Rejected', Status.REJECTED)
    ];

    readonly roleOptions: DropdownItem[] = [
        new DropdownItem('Super Admin', Role.PAY10_ADMIN),
        new DropdownItem('Admin', Role.DEVELOPER_ADMIN),
        new DropdownItem('Developer', Role.DEVELOPER)
    ];

    get currentDropdownOptions(): DropdownItem[] {
        switch (this.filterField) {
            case FilterField.STATUS:
                return this.statusOptions;
            case FilterField.ROLE:
                return this.roleOptions;
            default:
                return [];
        }
    }

    private getAllDropdownItems(items: DropdownItem[]): DropdownItem[] {
        let allItems: DropdownItem[] = [];
        for (const item of items) {
            allItems.push(item);
            if (item.hasChildren()) {
                allItems.push(...this.getAllDropdownItems(item.children!));
            }
        }
        return allItems;
    }

    toggleItemExpansion(item: DropdownItem) {
        item.expanded = !item.expanded;
    }

    private selectAllChildren(item: DropdownItem, selected: boolean) {
        if (item.hasChildren()) {
            item.children!.forEach(child => {
                child.selected = selected;
                if (child.value !== undefined) {
                    if (selected) {
                        this.dropdownFilterValues.add(child.value);
                    } else {
                        this.dropdownFilterValues.delete(child.value);
                    }
                }
                this.selectAllChildren(child, selected);
            });
        }
    }

    private updateParentSelection(allOptions: DropdownItem[]) {
        const processItem = (item: DropdownItem) => {
            if (item.hasChildren()) {
                item.children!.forEach(child => processItem(child));

                const allChildrenSelected = item.children!.every(child => child.selected);
                const noChildrenSelected = item.children!.every(child => !child.selected);

                if (allChildrenSelected) {
                    item.selected = true;
                    if (item.value !== undefined) {
                        this.dropdownFilterValues.add(item.value);
                    }
                } else if (noChildrenSelected) {
                    item.selected = false;
                    if (item.value !== undefined) {
                        this.dropdownFilterValues.delete(item.value);
                    }
                }
            }
        };

        allOptions.forEach(item => processItem(item));
    }

    toggleDropdownFilterValue(item: DropdownItem, ev: Event) {
        item.selected = !item.selected;

        if (item.value !== undefined) {
            if (item.selected) {
                this.dropdownFilterValues.add(item.value);
            } else {
                this.dropdownFilterValues.delete(item.value);
            }
        }

        if (item.hasChildren()) {
            this.selectAllChildren(item, item.selected);
        }

        this.updateParentSelection(this.currentDropdownOptions);

        this.applyCurrentFilter();
    }

    searchTextInDropdownMenu: string = '';

    searchInDropdownMenu() {
        const searchTerm = (this.searchTextInDropdownMenu || '').trim().toLowerCase();

        const allItems = this.getAllDropdownItems(this.currentDropdownOptions);
        allItems.forEach(item => {
            if (searchTerm === '') {
                item.visible = true;
            } else {
                item.visible = item.label.toLowerCase().includes(searchTerm);
            }
        });

        if (searchTerm !== '') {
            this.expandParentsOfVisibleItems(this.currentDropdownOptions);
        }
    }

    private expandParentsOfVisibleItems(items: DropdownItem[]) {
        for (const item of items) {
            if (item.hasChildren()) {
                const hasVisibleChild = item.children!.some(child =>
                    child.visible || (child.hasChildren() && this.hasVisibleDescendant(child))
                );
                if (hasVisibleChild) {
                    item.visible = true;
                    item.expanded = true;
                }
                this.expandParentsOfVisibleItems(item.children!);
            }
        }
    }

    private hasVisibleDescendant(item: DropdownItem): boolean {
        if (!item.hasChildren()) {
            return false;
        }
        return item.children!.some(child =>
            child.visible || this.hasVisibleDescendant(child)
        );
    }

    selectAllMenuItems() {
        const allItems = this.getAllDropdownItems(this.currentDropdownOptions);
        const allVisibleItems = allItems.filter(item => item.visible);

        if (this.dropdownFilterValues.size < allVisibleItems.length) {
            allVisibleItems.forEach(item => {
                this.dropdownFilterValues.add(item.value);
            });
            this.applyCurrentFilter();
        }
        allVisibleItems.forEach(item => {
            item.selected = true;
        });
    }

    deselectAllMenuItems() {
        const allItems = this.getAllDropdownItems(this.currentDropdownOptions);
        const allVisibleItems = allItems.filter(item => item.visible);

        if (this.dropdownFilterValues.size > 0) {
            allVisibleItems.forEach(item => {
                this.dropdownFilterValues.delete(item.value);
            });
            this.applyCurrentFilter();
        }
        allVisibleItems.forEach(item => {
            item.selected = false;
        });
    }

    showSelectedMenuItem: boolean = false;

    toggleShowSelectedMenuItem() {
        this.showSelectedMenuItem = !this.showSelectedMenuItem;
        const allItems = this.getAllDropdownItems(this.currentDropdownOptions);

        if (this.showSelectedMenuItem) {
            allItems.forEach(item => {
                item.visible = item.selected;
            });
            this.expandParentsOfSelectedItems(this.currentDropdownOptions);
        } else {
            allItems.forEach(item => {
                item.visible = true;
            });
        }
    }

    private expandParentsOfSelectedItems(items: DropdownItem[]) {
        for (const item of items) {
            if (item.hasChildren()) {
                const hasSelectedChild = item.children!.some(child =>
                    child.selected || (child.hasChildren() && this.hasSelectedDescendant(child))
                );
                if (hasSelectedChild) {
                    item.visible = true;
                    item.expanded = true;
                }
                this.expandParentsOfSelectedItems(item.children!);
            }
        }
    }

    private hasSelectedDescendant(item: DropdownItem): boolean {
        if (!item.hasChildren()) {
            return false;
        }
        return item.children!.some(child =>
            child.selected || this.hasSelectedDescendant(child)
        );
    }

    applyCurrentFilter() {
        if (!this.filterField) return;

        let filterValue: any = null;
        switch (this.filterField) {
            case FilterField.STATUS:
            case FilterField.ROLE:
                filterValue = new Set(this.dropdownFilterValues);
                break;
            case FilterField.NAME:
            case FilterField.EMAIL:
            case FilterField.MERCHANT_ID:
            case FilterField.MERCHANT_NAME:
                filterValue = this.textInputFilter;
                break;
        }

        if (filterValue) {
            if (filterValue instanceof Set && filterValue.size === 0) {
                this.filters.delete(this.filterField);
            } else {
                this.filters.set(this.filterField, filterValue);
            }
        } else {
            this.filters.delete(this.filterField);
        }

        this.popupManager.closeAll();
        this.fetchUsers();
    }

    get showCustomize(): boolean {
        return this.popupManager.isOpen(PopupType.CUSTOMIZE);
    }

    toggleCustomizeDropdown() {
        this.popupManager.toggle(PopupType.CUSTOMIZE);
        this.perfectScrollbarService.initializeScrollableList();
    }

    get filteredColumns(): ColDef[] {
        const q = (this.customizeSearch || '').trim().toLowerCase();
        return this.columnDefs.filter(col => {
            if ((col as any).colId === 'actions') return false;
            if (this.showSelectedFieldsOnly && col.hide) return false;
            if (!q) return true;
            const label = (col.headerName || col.field || '').toString().toLowerCase();
            return label.includes(q);
        });
    }

    customizeSearch = '';
    showSelectedFieldsOnly = false;

    selectAllColumns() {
        this.columnDefs = this.columnDefs.map(col => ({ ...col, hide: false }));

        if (this.gridApi) {
            const colIds = this.columnDefs.map(col => (col as any).colId).filter(Boolean);
            this.gridApi.setColumnsVisible(colIds, true);
        }
    }

    deselectAllColumns() {
        this.columnDefs = this.columnDefs.map(col => {
            if ((col as any).colId === 'actions') {
                return col;
            }
            return { ...col, hide: true };
        });

        if (this.gridApi) {
            const colIds = this.columnDefs.map(col => (col as any).colId).filter(colId => colId && colId !== 'actions');
            this.gridApi.setColumnsVisible(colIds, false);
        }
    }

    toggleShowSelectedFieldsOnly() {
        this.showSelectedFieldsOnly = !this.showSelectedFieldsOnly;
    }

    onToggleColumnById(colId: string, checked: boolean) {
        const idx = this.getColumnIndexById(colId);
        if (idx === -1) return;
        const updated = [...this.columnDefs];
        const target = { ...updated[idx], hide: !checked } as ColDef;
        updated[idx] = target;
        this.columnDefs = updated;

        if (this.gridApi) {
            this.gridApi.setColumnsVisible([colId], checked);
        }
    }

    private getColumnIndexById(colId: string): number {
        return this.columnDefs.findIndex(c => (c as any).colId === colId);
    }

    trackByFilter(index: number, item: [FilterField, any]): FilterField {
        return item[0];
    }

    getFiltersArray(): [FilterField, any][] {
        return Array.from(this.filters.entries());
    }

    getFilterDisplayText(filterKey: FilterField, filterValue: any): string {
        switch (filterKey) {
            case FilterField.STATUS:
            case FilterField.ROLE:
                if (filterValue instanceof Set && filterValue.size > 0) {
                    const values = Array.from(filterValue);
                    if (values.length === 1) {
                        return `${filterKey}: ${values[0]}`;
                    } else {
                        return `${filterKey}: ${values.length} selected`;
                    }
                }
                return `${filterKey}: None`;

            case FilterField.NAME:
            case FilterField.EMAIL:
            case FilterField.MERCHANT_ID:
            case FilterField.MERCHANT_NAME:
            default:
                return `${filterKey}: ${filterValue || ''}`;
        }
    }

    clearAllFilters() {
        this.initFilterValues();
        this.filters.clear();
        this.fetchUsers();
    }

    removeFilter(filterKey: FilterField) {
        this.initFilterValues();
        this.filters.delete(filterKey);
        this.fetchUsers();
    }

    getItemRangeInPage(): string {
        if (this.totalItems === 0) {
            return '0';
        }
        let start = ((this.currentPage - 1) * this.rowsPerPage) + 1;
        let end = Math.min(((start - 1) + (this.currentPage === 1 ? this.rowsPerPage : ((this.currentPage - 1) * this.rowsPerPage))), this.totalItems);
        return `${start} - ${end}`;
    }

    onPageChange(page: number) {
        this.currentPage = page;
        this.fetchUsers();
        if (this.gridApi) {
            this.gridApi.paginationGoToPage(page - 1);
        }
    }

    changeRowsPerPage(value: number) {
        this.rowsPerPage = Number(value);
        this.currentPage = 1;
        this.fetchUsers();
        if (this.gridApi) {
            this.gridApi.setGridOption('paginationPageSize', this.rowsPerPage);
            this.gridApi.paginationGoToFirstPage();
        }
    }
}
